import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { i18next } from '@yola/ws-sdk';
import utils from 'src/js/modules/utils';
import validatePageName from '../helpers/validate-page-name';
import validatePagePath from '../helpers/validate-page-path';
import validatePagePassword from '../helpers/validate-page-password';
import constants from '../constants';

const {
  Stack,
  Box,
  ControlGroup,
  FieldStatus,
  CardSelector,
  Paragraph,
  Heading,
  Divider,
  InputGroupField,
  CheckboxField,
  LanguageSelectField,
} = designSystem;

const { validateRequired } = utils.validators;
const { fieldNames } = constants;

function GeneralSection({
  control,
  fieldRefs,
  captions,
  pageId,
  pathPrefix,
  languageCodeOptions,
  isPathDisabled,
  isDisplayInNavigationAvailable,
  isPasswordProtected,
  isPasswordProtectedPagesFeatureAvailable,
  isPasswordProtectedPagesFeatureFlagEnabled,
  onEnablePasswordProtection,
  onDisablePasswordProtection,
}) {
  const [passwordVisibility, setPasswordVisibility] = useState(false);

  const switchPasswordVisibility = () => {
    setPasswordVisibility((prev) => !prev);
  };

  return (
    <Stack gap="spacing-m">
      <ControlGroup title={captions.pageName.name}>
        <div ref={fieldRefs[fieldNames.NAME]} className="ws-page-settings__field-wrapper">
          <InputGroupField
            name={fieldNames.NAME}
            control={control}
            rules={{
              validate: validatePageName,
            }}
          />
        </div>
        <Box marginTop="spacing-3-xs">
          <div ref={fieldRefs[fieldNames.PATH]} className="ws-page-settings__field-wrapper">
            <InputGroupField
              name={fieldNames.PATH}
              control={control}
              rules={{
                validate: (value) => validatePagePath(value, pageId),
              }}
              size="tiny"
              iconGlyph="link"
              prefixText={pathPrefix}
              disabled={isPathDisabled}
            />
          </div>
        </Box>
      </ControlGroup>

      {isDisplayInNavigationAvailable && (
        <CheckboxField
          name={fieldNames.DISPLAY_IN_NAVIGATION}
          control={control}
          label={captions.displayInNavigation.label}
        />
      )}

      <div>
        <ControlGroup title={captions.pageLocale.name}>
          <LanguageSelectField
            name={fieldNames.HTML_LOCALE}
            control={control}
            rules={{
              validate: validateRequired,
            }}
            appearance="searchable"
            options={languageCodeOptions}
            placeholder={i18next.t(captions.pageLocale.placeholder)}
          />
        </ControlGroup>
        <FieldStatus text={captions.pageLocale.description} />
      </div>

      {isPasswordProtectedPagesFeatureFlagEnabled && (
        <React.Fragment>
          <div>
            <Heading type="heading-5">{captions.passwordProtectedPages.title}</Heading>
            <Box marginTop="spacing-4-xs" marginBottom="spacing-xs">
              <Divider />
            </Box>
            <Paragraph size="small" appearance="medium-emphasis">
              {captions.passwordProtectedPages.subtitle}
            </Paragraph>
          </div>

          <div className="ws-page-settings__password-protection-switcher">
            <CardSelector
              selected={!isPasswordProtected}
              iconGlyph="globe"
              verticalAlign="center"
              title={captions.passwordProtectedPages.openedOptionLabel}
              onClick={onDisablePasswordProtection}
            />
            <CardSelector
              selected={isPasswordProtected}
              iconGlyph="unlock"
              verticalAlign="center"
              title={captions.passwordProtectedPages.protectedOptionLabel}
              premium={!isPasswordProtectedPagesFeatureAvailable}
              onClick={onEnablePasswordProtection}
            />
          </div>

          {isPasswordProtected && (
            <div>
              <Heading type="heading-6">{captions.passwordProtectedPages.fieldTitle}</Heading>
              <Box marginTop="spacing-4-xs">
                <Paragraph size="small" appearance="medium-emphasis">
                  {captions.passwordProtectedPages.fieldSubtitle}
                </Paragraph>
              </Box>
              <Box marginTop="spacing-2-xs" marginBottom="spacing-3-xs">
                <div
                  ref={fieldRefs[fieldNames.PASSWORD]}
                  className="ws-page-settings__field-wrapper"
                >
                  <InputGroupField
                    name={fieldNames.PASSWORD}
                    control={control}
                    rules={{
                      validate: validatePagePassword,
                    }}
                    type={passwordVisibility ? 'text' : 'password'}
                    placeholder={captions.passwordProtectedPages.fieldPlaceholder}
                    iconButtonGlyph={passwordVisibility ? 'eye-off' : 'eye'}
                    disabled={!isPasswordProtectedPagesFeatureAvailable}
                    maxLength={50}
                    onIconButtonClick={switchPasswordVisibility}
                  />
                </div>
              </Box>
            </div>
          )}
        </React.Fragment>
      )}
    </Stack>
  );
}

GeneralSection.propTypes = {
  control: PropTypes.shape().isRequired,
  fieldRefs: PropTypes.shape().isRequired,
  captions: PropTypes.shape().isRequired,
  pageId: PropTypes.string.isRequired,
  pathPrefix: PropTypes.string.isRequired,
  languageCodeOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isPathDisabled: PropTypes.bool.isRequired,
  isDisplayInNavigationAvailable: PropTypes.bool.isRequired,
  isPasswordProtected: PropTypes.bool.isRequired,
  isPasswordProtectedPagesFeatureAvailable: PropTypes.bool.isRequired,
  isPasswordProtectedPagesFeatureFlagEnabled: PropTypes.bool.isRequired,
  onEnablePasswordProtection: PropTypes.func.isRequired,
  onDisablePasswordProtection: PropTypes.func.isRequired,
};

export default GeneralSection;
